import { cn } from '@flatfile/baseline'

interface ThumbnailProps {
  stats?: {
    fileCount: number
    totalRecords: number
    totalColumns: number
    totalSheets: number
    totalArtifacts: number
    dataQuality?: number
    projectId?: string
  }
  className?: string
}

// Variant 1: Abstract Data Pattern
export function DataPatternThumbnail({ stats, className }: ThumbnailProps) {
  const generatePattern = () => {
    const elements = []
    const hasData = stats && stats.totalRecords > 0

    // Create a unique pattern based on project stats
    const seed = stats ? stats.totalRecords + stats.totalColumns : 0
    const colors = [
      'bg-blue-400',
      'bg-purple-400',
      'bg-indigo-400',
      'bg-cyan-400',
    ]

    for (let i = 0; i < 12; i++) {
      const colorIndex = (seed + i) % colors.length
      const height = hasData ? 20 + ((seed * i) % 60) : 20
      const delay = i * 50

      elements.push(
        <div
          key={i}
          className={cn(
            'w-full rounded-full transition-all duration-700',
            hasData ? colors[colorIndex] : 'bg-gray-200',
            !stats && 'animate-pulse'
          )}
          style={{
            height: `${height}%`,
            animationDelay: `${delay}ms`,
          }}
        />
      )
    }

    return elements
  }

  return (
    <div
      className={cn(
        'h-40 relative bg-gradient-to-br from-gray-50 to-gray-100 rounded-t-lg overflow-hidden',
        className
      )}
    >
      <div className='absolute inset-0 flex items-end gap-1 p-4'>
        {generatePattern()}
      </div>

      {/* Stats grid overlay */}
      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white/90 to-transparent p-3'>
        <div className='grid grid-cols-4 gap-2'>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.fileCount || 0}
            </div>
            <div className='text-xs text-gray-600'>Files</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalSheets || 0}
            </div>
            <div className='text-xs text-gray-600'>Sheets</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalColumns || 0}
            </div>
            <div className='text-xs text-gray-600'>Fields</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalArtifacts || 0}
            </div>
            <div className='text-xs text-gray-600'>Artifacts</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Variant 2: Circular Stats Display
export function CircularStatsThumbnail({ stats, className }: ThumbnailProps) {
  const dataQuality = stats?.dataQuality || 0
  const circumference = 2 * Math.PI * 40 // radius = 40
  const strokeDashoffset = circumference - (dataQuality / 100) * circumference

  return (
    <div
      className={cn(
        'h-40 relative bg-gradient-to-br from-blue-50 to-indigo-50 rounded-t-lg overflow-hidden',
        className
      )}
    >
      {/* Background circles */}
      <div className='absolute inset-0 flex items-center justify-center pt-2'>
        <div className='w-20 h-20 rounded-full bg-white/50 absolute animate-pulse' />
        <div className='w-16 h-16 rounded-full bg-white/70 absolute' />
      </div>

      {/* Stats circle */}
      <div className='absolute inset-0 flex items-center justify-center pt-2'>
        <svg className='w-20 h-20 transform -rotate-90' viewBox='0 0 100 100'>
          <circle
            cx='50'
            cy='50'
            r='40'
            stroke='#e5e7eb'
            strokeWidth='8'
            fill='none'
          />
          <circle
            cx='50'
            cy='50'
            r='40'
            stroke={
              dataQuality >= 90
                ? '#10b981'
                : dataQuality >= 70
                  ? '#f59e0b'
                  : '#ef4444'
            }
            strokeWidth='8'
            fill='none'
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className='transition-all duration-1000'
          />
        </svg>

        {/* Center content */}
        <div className='absolute text-center'>
          <div className='text-xl font-bold text-gray-800'>
            {stats?.totalRecords || 0}
          </div>
          <div className='text-xs text-gray-500'>records</div>
        </div>
      </div>

      {/* Stats grid overlay */}
      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-white/90 to-transparent p-3'>
        <div className='grid grid-cols-4 gap-2'>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.fileCount || 0}
            </div>
            <div className='text-xs text-gray-600'>Files</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalSheets || 0}
            </div>
            <div className='text-xs text-gray-600'>Sheets</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalColumns || 0}
            </div>
            <div className='text-xs text-gray-600'>Fields</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-gray-800'>
              {stats?.totalArtifacts || 0}
            </div>
            <div className='text-xs text-gray-600'>Artifacts</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Variant 3: Heatmap Grid
export function HeatmapThumbnail({ stats, className }: ThumbnailProps) {
  const generateHeatmap = () => {
    const cells = []
    const gridSize = 8
    const hasData = stats && stats.totalRecords > 0

    for (let i = 0; i < gridSize * gridSize; i++) {
      const row = Math.floor(i / gridSize)
      const col = i % gridSize

      // Create a pattern based on stats
      const intensity = hasData
        ? (Math.sin(row * 0.5 + (stats.totalRecords % 10)) +
            Math.cos(col * 0.5 + (stats.totalColumns % 10))) /
          2
        : 0

      const opacity = Math.abs(intensity)

      cells.push(
        <div
          key={i}
          className={cn(
            'aspect-square rounded-sm transition-all duration-500',
            hasData
              ? 'bg-gradient-to-br from-blue-500 to-purple-500'
              : 'bg-gray-200',
            !stats && 'animate-pulse'
          )}
          style={{
            opacity: hasData ? 0.2 + opacity * 0.8 : 0.2,
            animationDelay: `${i * 20}ms`,
          }}
        />
      )
    }

    return cells
  }

  return (
    <div
      className={cn(
        'h-40 relative bg-gradient-to-br from-gray-900 to-gray-800 rounded-t-lg overflow-hidden',
        className
      )}
    >
      <div className='absolute inset-0 p-4 pb-16'>
        <div className='grid grid-cols-8 gap-0.5 h-full'>
          {generateHeatmap()}
        </div>
      </div>

      {/* Stats grid overlay */}
      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-3'>
        <div className='grid grid-cols-4 gap-2'>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.fileCount || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Files</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalSheets || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Sheets</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalColumns || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Fields</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalArtifacts || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Artifacts</div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Variant 4: Wave Pattern with unique colors per project
export function WavePatternThumbnail({ stats, className }: ThumbnailProps) {
  const hasData = stats && stats.totalRecords > 0

  // Generate unique but consistent values based on project ID
  const projectId = stats?.projectId || ''
  const hashCode = projectId.split('').reduce((acc: number, char: string) => {
    return char.charCodeAt(0) + ((acc << 5) - acc)
  }, 0)

  // Create unique wave characteristics
  const waveHeight = hasData
    ? 20 + Math.min(stats.totalRecords / 50, 60) + (Math.abs(hashCode) % 20)
    : 20

  // Generate unique color scheme based on project ID
  const colorSchemes = [
    {
      from: 'from-blue-50',
      to: 'to-blue-100',
      waves: ['bg-blue-200', 'bg-blue-300', 'bg-blue-400'],
    },
    {
      from: 'from-purple-50',
      to: 'to-purple-100',
      waves: ['bg-purple-200', 'bg-purple-300', 'bg-purple-400'],
    },
    {
      from: 'from-emerald-50',
      to: 'to-emerald-100',
      waves: ['bg-emerald-200', 'bg-emerald-300', 'bg-emerald-400'],
    },
    {
      from: 'from-amber-50',
      to: 'to-amber-100',
      waves: ['bg-amber-200', 'bg-amber-300', 'bg-amber-400'],
    },
    {
      from: 'from-rose-50',
      to: 'to-rose-100',
      waves: ['bg-rose-200', 'bg-rose-300', 'bg-rose-400'],
    },
    {
      from: 'from-cyan-50',
      to: 'to-cyan-100',
      waves: ['bg-cyan-200', 'bg-cyan-300', 'bg-cyan-400'],
    },
    {
      from: 'from-indigo-50',
      to: 'to-indigo-100',
      waves: ['bg-indigo-200', 'bg-indigo-300', 'bg-indigo-400'],
    },
    {
      from: 'from-teal-50',
      to: 'to-teal-100',
      waves: ['bg-teal-200', 'bg-teal-300', 'bg-teal-400'],
    },
  ]

  const colorScheme = colorSchemes[Math.abs(hashCode) % colorSchemes.length]

  // Generate unique wave patterns
  const waveOffset1 = 15 + (Math.abs(hashCode >> 8) % 10)
  const waveOffset2 = 20 + (Math.abs(hashCode >> 16) % 10)
  const waveOffset3 = 25 + (Math.abs(hashCode >> 24) % 10)

  return (
    <div
      className={cn(
        `h-40 relative bg-gradient-to-b ${colorScheme.from} ${colorScheme.to} rounded-t-lg overflow-hidden`,
        className
      )}
    >
      {/* Wave layers with unique patterns */}
      <div
        className={cn(
          `absolute bottom-0 left-0 right-0 ${colorScheme.waves[0]} transition-all duration-1000`,
          !stats && 'animate-pulse'
        )}
        style={{
          height: `${waveHeight}%`,
          clipPath: `polygon(0 ${waveOffset1}%, 25% 0%, 50% ${waveOffset1}%, 75% 0%, 100% ${waveOffset1}%, 100% 100%, 0 100%)`,
        }}
      />
      <div
        className={cn(
          `absolute bottom-0 left-0 right-0 ${colorScheme.waves[1]} transition-all duration-1000 delay-100`,
          !stats && 'animate-pulse'
        )}
        style={{
          height: `${waveHeight * 0.7}%`,
          clipPath: `polygon(0 0%, 25% ${waveOffset2}%, 50% 0%, 75% ${waveOffset2}%, 100% 0%, 100% 100%, 0 100%)`,
        }}
      />
      <div
        className={cn(
          `absolute bottom-0 left-0 right-0 ${colorScheme.waves[2]} transition-all duration-1000 delay-200`,
          !stats && 'animate-pulse'
        )}
        style={{
          height: `${waveHeight * 0.5}%`,
          clipPath: `polygon(0 ${waveOffset3}%, 33% 0%, 66% ${waveOffset3}%, 100% 0%, 100% 100%, 0 100%)`,
        }}
      />

      {/* Stats grid overlay */}
      <div className='absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/20 to-transparent p-3'>
        <div className='grid grid-cols-4 gap-2'>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.fileCount || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Files</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalSheets || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Sheets</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalColumns || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Fields</div>
          </div>
          <div className='text-center'>
            <div className='text-lg font-bold text-white drop-shadow-md'>
              {stats?.totalArtifacts || 0}
            </div>
            <div className='text-xs text-white/90 drop-shadow'>Artifacts</div>
          </div>
        </div>
      </div>
    </div>
  )
}
